<template>
  <Modal
    :aria-label="addOn.name[activeLocale]"
    :show="show"
    :show-header="false"
    :wrapper-class="[
      'ticket-selection__bookable-modal with-date-options-modal md:w-max! md:min-w-[516px]! slot-based-modal max-w-full! md:max-w-[98%]! rounded-none! md:rounded-[10px]! ml-0!',
      isDark ? 'dark-theme' : '',
    ]"
    container-class="py-0! md:py-10! items-center!"
    body-class="p-6 min-h-screen md:min-h-0 flex flex-col gap-4"
    @close="emit('close')"
  >
    <div class="ticket-selection__bookable-modal-header">
      <div>
        <div class="flex items-center gap-3">
          <FontAwesomeIcon :icon="['far', 'building']" class="text-lg" />
          <p class="ticket-selection__bookable-modal-title">
            {{ addOn.name[activeLocale] }}
          </p>
        </div>
        <p class="ticket-selection__bookable-modal-description">
          {{ addOn.description[activeLocale] }}
        </p>
      </div>
      <button
        type="button"
        aria-label="close icon"
        class="cursor-pointer"
        @click="emit('close')"
      >
        <FontAwesomeIcon
          :icon="['fal', 'times']"
          :class="isDark ? 'text-white/80' : 'text-[#3d4146]'"
        />
      </button>
    </div>

    <div
      class="flex-1 md:flex-none flex flex-col md:flex-row md:justify-center gap-6 min-h-0"
    >
      <div class="md:flex-1 shrink-0">
        <DatePicker
          v-model="selectedDate"
          :time-zone="eventSettings?.timezone"
          :min-date="new Date()"
          inline
          auto-apply
          :enabled-dates="enabledDates"
          :hide-offset-dates="true"
          class="h-full"
        >
          <template #day="{ day, date }">
            <span :class="{ 'slot-date-sold-out': isSoldOutDate(date) }">
              {{ day }}
            </span>
          </template>
        </DatePicker>
      </div>

      <div v-if="selectedDate" class="addon-slots-container">
        <div
          :class="[
            'md:rounded-md pt-6 flex-1 flex flex-col gap-4 min-h-0',
            isDark ? 'bg-white/5' : 'bg-[#F8F8F8]',
          ]"
        >
          <p class="ticket-selection__bookable-modal-slots-label">
            {{ $t('landingpage.checkout-page.slot-based.slots.label') }}
          </p>

          <p
            v-if="
              addOn.max_quantity_per_booking != null &&
              bookingTotal >= addOn.max_quantity_per_booking
            "
            class="ticket-selection__bookable-modal-slots-max-booking-info italic"
          >
            {{
              $t(
                'landingpage.checkout-page.slot-based.slots.booking-limit-reached',
                {
                  used: bookingTotal,
                  max: addOn.max_quantity_per_booking,
                },
              )
            }}
          </p>

          <div class="slots-options-container">
            <div
              v-for="slot in selectedDateSlots"
              :key="slot.start_at"
              class="slots-option-wrapper"
              :class="[
                isSelectedOption(slot) || isAlreadyBooked(slot)
                  ? 'slots-option-wrapper--selected'
                  : '',
                isAlreadyBooked(slot) ||
                slot.quantity.remaining === 0 ||
                slot.isPast
                  ? 'opacity-40'
                  : '',
                !isSelectedOption(slot) &&
                !isAlreadyBooked(slot) &&
                !slot.isPast &&
                (selectedOptionValue.length >= maxSelections ||
                  slot.quantity.remaining === 0)
                  ? 'opacity-40'
                  : '',
              ]"
            >
              <div class="slot-header-badges">
                <div class="slot-header-badge-timezone">
                  <FontAwesomeIcon :icon="['far', 'clock']" class="w-2 h-2" />
                  {{ eventSettings?.timezone }}
                </div>
                <div class="slot-header-badge-available">
                  {{ slot.quantity.remaining }} {{ $t('common.available') }}
                </div>
              </div>
              <div class="slot-info">
                <p class="slot-info-time">{{ slot.label }}</p>
                <div class="flex items-center gap-2">
                  <p class="slot-info-price">
                    {{
                      formatCurrencyLeft(
                        slot.price.gross,
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                        orderDetails?.currency,
                      )
                    }}
                  </p>
                  <div
                    v-if="isSelectedOption(slot) || isAlreadyBooked(slot)"
                    class="slot-option-selected-icon"
                  >
                    <FontAwesomeIcon :icon="['fas', 'check']" />
                  </div>
                </div>
              </div>

              <div class="slot-btn-container">
                <button
                  v-if="isAlreadyBooked(slot)"
                  class="slot-btn-add-remove"
                  disabled
                >
                  {{ $t('landingpage.checkout-page.slot-based.slots.choose') }}
                </button>

                <button
                  v-else-if="isSelectedOption(slot)"
                  class="slot-btn-add-remove"
                  @click="
                    selectedOptionValue = selectedOptionValue.filter(
                      (s) =>
                        s.start_at !== slot.start_at ||
                        s.end_at !== slot.end_at,
                    )
                  "
                >
                  {{ $t('landingpage.checkout-page.slot-based.slots.remove') }}
                </button>

                <button
                  v-else
                  class="slot-btn-add-remove"
                  :disabled="
                    slot.isPast ||
                    selectedOptionValue.length >= maxSelections ||
                    slot.quantity.remaining === 0
                  "
                  @click="selectedOptionValue.push(slot)"
                >
                  {{ $t('landingpage.checkout-page.slot-based.slots.choose') }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="selectedOptionValue.length"
          :class="[
            'fixed bottom-0 left-0 right-0 z-10 px-3 py-4 md:p-6 md:static md:rounded-md',
            isDark ? 'bg-white/5' : 'bg-[#F8F8F8]',
          ]"
        >
          <button
            type="button"
            class="flex w-full items-center justify-between md:hidden"
            @click="toggleOverview()"
          >
            <p class="ticket-selection__bookable-modal-slots-overview-label">
              {{
                $t('landingpage.checkout-page.slot-based.slots.overview-label')
              }}
            </p>
            <FontAwesomeIcon
              :icon="[
                'far',
                isOverviewExpanded ? 'chevron-down' : 'chevron-up',
              ]"
              class="text-sm"
            />
          </button>
          <p
            class="ticket-selection__bookable-modal-slots-overview-label hidden md:block"
          >
            {{
              $t('landingpage.checkout-page.slot-based.slots.overview-label')
            }}
          </p>
          <div
            class="mt-2 flex flex-col gap-2"
            :class="{ 'hidden md:flex': !isOverviewExpanded }"
          >
            <div
              v-for="selected in selectedOptionValue"
              :key="selected.start_at"
              class="flex items-center justify-between gap-4"
            >
              <p :class="['text-sm!', isDark ? 'text-white' : 'text-black']">
                {{ selected.label }}
              </p>
              <p :class="['text-sm!', isDark ? 'text-white' : 'text-black']">
                {{
                  formatCurrencyLeft(
                    selected.price.gross,
                    { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                    orderDetails?.currency,
                  )
                }}
              </p>
            </div>
          </div>
          <div class="ticket-selection__bookable-modal-btn-container">
            <button
              type="button"
              class="ticket-selection__bookable-modal-btn-cancel btn"
              @click="emit('close')"
            >
              {{ $t('common.cancel') }}
            </button>
            <button
              type="button"
              class="ticket-selection__bookable-modal-btn-add btn"
              :disabled="selectedOptionValue.length === 0"
              @click="emit('confirm', selectedOptionValue)"
            >
              <FontAwesomeIcon :icon="['far', 'check']" />
              {{
                $t(
                  'landingpage.checkout-page.ticket-selection.bookables.modal.add',
                )
              }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="!selectedOptionValue.length"
      class="ticket-selection__bookable-modal-btn-container md:hidden! flex-none! border-none!"
    >
      <button
        type="button"
        class="ticket-selection__bookable-modal-btn-cancel btn"
        @click="emit('close')"
      >
        {{ $t('common.cancel') }}
      </button>
    </div>
  </Modal>
</template>

<script setup>
import { ref, inject, computed } from 'vue'
import Modal from '@/Components/Modal.vue'
import { customFormat, format } from '@/composables/date'
import { useCurrentLocaleCode } from '@/composables/locale'
import { formatCurrencyLeft } from '@/composables/currency'
import { toZonedTime } from 'date-fns-tz'
import DatePicker from '@/Components/common/DatePicker.vue'
import '@vuepic/vue-datepicker/dist/main.css'

const props = defineProps({
  show: { type: Boolean, default: () => true },
  addOn: { type: Object, default: () => {} },
})

const emit = defineEmits(['close', 'confirm'])

const activeLocale = inject('activeLocale')
const orderDetails = inject('orderDetails')
const eventSettings = inject('eventSettings')
const sectionThemeClass = inject('sectionThemeClass', ref(''))
const isDark = computed(() => sectionThemeClass.value === 'dark')

const selectedOptionValue = ref([])
const selectedDate = ref('')
const isOverviewExpanded = ref(true)
const toggleOverview = () =>
  (isOverviewExpanded.value = !isOverviewExpanded.value)
const enabledDates = ref([])
const soldOutDates = ref([])
const availableSlots = ref([])

const groupAvailabilities = () => {
  const map = {}

  ;(props.addOn?.options ?? []).forEach((item) => {
    const start = new Date(item.start_at)
    const end = new Date(item.end_at)

    const zonedStart = toZonedTime(start, eventSettings?.timezone)
    const dateKey = customFormat(zonedStart, 'yyyy-MM-dd')
    const locale = useCurrentLocaleCode()
    const tz = eventSettings?.timezone
    const formatSlotTime = (date) =>
      format(date, {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: tz,
        ...(locale.startsWith('en') ? { hourCycle: 'h12' } : {}),
      })
    const startTime = locale.startsWith('en')
      ? formatSlotTime(start).replace(/\s?(AM|PM)$/i, '')
      : formatSlotTime(start)
    const endTime = formatSlotTime(end)
    const timeSuffix = locale.startsWith('de') ? ' Uhr' : ''
    const timeLabel = `${startTime} - ${endTime}${timeSuffix}`

    if (!map[dateKey]) {
      map[dateKey] = []
    }

    map[dateKey].push({
      label: timeLabel,
      start_at: item.start_at,
      end_at: item.end_at,
      price: item.price,
      quantity: item.quantity,
    })
  })

  availableSlots.value = Object.entries(map).map(([date, slots]) => {
    const sortedSlots = slots.sort(
      (a, b) => new Date(a.start_at) - new Date(b.start_at),
    )
    const now = new Date()
    const hasAvailable = sortedSlots.some(
      (slot) => slot.quantity.remaining > 0 && new Date(slot.start_at) >= now,
    )
    enabledDates.value.push(date)
    if (!hasAvailable) {
      soldOutDates.value.push(date)
    }
    return {
      date,
      slots: sortedSlots,
    }
  })
}

groupAvailabilities()

const isSoldOutDate = (date) => {
  const today = customFormat(new Date(), 'yyyy-MM-dd')
  const dateKey = customFormat(date, 'yyyy-MM-dd')
  if (dateKey < today) {
    return false
  }
  return soldOutDates.value.includes(dateKey)
}

const selectedDateSlots = computed(() => {
  if (!selectedDate.value) {
    return []
  }
  const dateKey = customFormat(
    toZonedTime(new Date(selectedDate.value), eventSettings?.timezone),
    'yyyy-MM-dd',
  )
  const slots =
    availableSlots.value.find((s) => s.date === dateKey)?.slots || []

  const now = new Date()
  return slots.map((slot) => ({
    ...slot,
    isPast: new Date(slot.start_at) < now,
  }))
})

const getBookingTotalForAddOn = () => {
  if (!orderDetails.value?.line_items) return 0
  return orderDetails.value.line_items
    .filter(({ product_type }) => product_type.includes('UniversalBookable'))
    .reduce((total, lineItem) => {
      const matching =
        lineItem.bookable_reservations?.filter(
          (r) => r.product?.id === props.addOn.id,
        ) ?? []
      return total + matching.reduce((sum, r) => sum + (r.quantity ?? 1), 0)
    }, 0)
}

const bookingTotal = computed(
  () => getBookingTotalForAddOn() + selectedOptionValue.value.length,
)

const maxSelections = computed(() => {
  const guestItems = (props.addOn.items || []).length

  const perGuestRemaining =
    props.addOn.max_quantity_per_guest == null
      ? Infinity
      : Math.max(0, props.addOn.max_quantity_per_guest - guestItems)

  const perBookingRemaining =
    props.addOn.max_quantity_per_booking == null
      ? Infinity
      : Math.max(
          0,
          props.addOn.max_quantity_per_booking - getBookingTotalForAddOn(),
        )

  return Math.min(perGuestRemaining, perBookingRemaining)
})

const isAlreadyBooked = (slot) => {
  return (props.addOn.items || []).some(
    (item) => item.starts_at === slot.start_at && item.ends_at === slot.end_at,
  )
}

const isSelectedOption = (slot) => {
  return selectedOptionValue.value.some(
    (s) => s.start_at === slot.start_at && s.end_at === slot.end_at,
  )
}
</script>
